import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, RotateCcw, ShoppingCart, X, AlertTriangle, CheckCircle2, ArrowRight, AlarmClock } from "lucide-react";
import { api, apiError, fmt, fmtPerUnit } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";

const REORDERABLE = new Set(["confirmed", "packed", "dispatched", "in_transit", "out_for_delivery", "delivered"]);

function ReorderResult({ result, onClose }) {
  const navigate = useNavigate();
  const nothingAdded = !result.added.length && !result.adjusted.length;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()} data-testid="reorder-dialog" data-lenis-prevent
        className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-2xl border border-line bg-surface p-6">
        <div className="flex items-start justify-between gap-4">
          <h3 className="font-display text-xl font-semibold tracking-tight text-1">
            {nothingAdded ? "Nothing could be added" : "Order added to your cart"}
          </h3>
          <button onClick={onClose} aria-label="Close" data-testid="reorder-dialog-close"
            className="rounded-lg p-1 text-3 transition-colors hoverable-t1">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mt-1 text-xs text-3">
          From order {result.order_number} — priced at today's rates. Review everything before you pay.
        </p>

        {result.added.length > 0 && (
          <ul className="mt-4 space-y-2" data-testid="reorder-added-list">
            {result.added.map((a) => (
              <li key={`${a.product_id}-${a.sku}`} className="flex items-start gap-2.5 text-sm text-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                <span>
                  {a.name} ({a.variant}) — {a.qty} {a.unit}{a.qty > 1 && a.unit === "pc" ? "s" : ""} at {fmtPerUnit(a.unit_price, a.unit)}
                  {a.already_in_cart && <span className="text-3"> · already in your cart</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
        {result.adjusted.length > 0 && (
          <ul className="mt-3 space-y-2" data-testid="reorder-adjusted-list">
            {result.adjusted.map((a) => (
              <li key={`${a.product_id}-${a.sku}`} className="flex items-start gap-2.5 text-sm text-amber-200/90">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                <span>{a.name} ({a.variant}) — added as {a.qty} {a.unit}{a.qty > 1 && a.unit === "pc" ? "s" : ""}: {a.reason}</span>
              </li>
            ))}
          </ul>
        )}
        {result.skipped.length > 0 && (
          <ul className="mt-3 space-y-2" data-testid="reorder-skipped-list">
            {result.skipped.map((s, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-red-300/90">
                <X className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
                <span>{s.name}{s.variant ? ` (${s.variant})` : ""} — {s.reason}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-6 flex gap-3">
          {!nothingAdded && (
            <button onClick={() => navigate("/cart")} data-testid="reorder-review-cart"
              className="flex h-11 flex-1 items-center justify-center gap-2 rounded-full bg-amber-500 text-sm font-bold text-black transition-colors hover:bg-amber-400">
              Review Cart <ArrowRight className="h-4 w-4" />
            </button>
          )}
          <button onClick={onClose} data-testid="reorder-continue"
            className={`h-11 rounded-full border border-strong px-5 text-sm font-semibold text-2 transition-colors hoverable-strong hoverable-t1 ${nothingAdded ? "flex-1" : ""}`}>
            Continue Shopping
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export function ReorderButton({ orderId, orderNumber, status, prominent = false }) {
  const { user } = useAuth();
  const { refreshCart } = useStore();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (!REORDERABLE.has(status)) return null;

  const reorder = async (e) => {
    e.stopPropagation();
    if (busy) return;
    if (!user) {
      toast.info("Sign in to reorder", { description: "Your past order will be waiting in your account." });
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/reorder`);
      await refreshCart();
      setResult(data);
    } catch (err) {
      toast.error(apiError(err, "Couldn't reorder this order"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button onClick={reorder} disabled={busy} data-testid={`reorder-button-${orderNumber || orderId}`}
        className={prominent
          ? "flex h-12 items-center justify-center gap-2 rounded-full bg-amber-500 px-7 text-sm font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-40"
          : "flex h-9 items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-4 text-xs font-bold text-amber-300 transition-colors hover:bg-amber-500/20 disabled:opacity-40"}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
        Reorder
      </button>
      <AnimatePresence>
        {result && <ReorderResult result={result} onClose={() => setResult(null)} />}
      </AnimatePresence>
    </>
  );
}

export function BuyAgainSection() {
  const { user } = useAuth();
  const { cart, addItem, setQty } = useStore();
  const navigate = useNavigate();
  const [products, setProducts] = useState(null);
  const [unavailable, setUnavailable] = useState([]);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/orders/buy-again")
      .then(({ data }) => { setProducts(data.products); setUnavailable(data.unavailable || []); })
      .catch(() => setProducts([]));
  }, [user]);

  if (!user || !products || (products.length === 0 && unavailable.length === 0)) return null;

  const quickAdd = async (p) => {
    if (busyId) return;
    setBusyId(p.product_id + p.sku);
    const moq = p.min_order_qty || 1;
    const inc = p.qty_increment || 1;
    let qty = Math.max(p.last_qty || 1, moq);
    if ((qty - moq) % inc) qty = moq + (Math.floor((qty - moq) / inc) + 1) * inc;
    // idempotent top-up: never stack beyond the intended quantity on repeat clicks
    const existing = (cart?.items || []).find((i) => i.product_id === p.product_id && i.sku === p.sku);
    if ((existing?.qty || 0) >= qty) {
      toast.info("Already in your cart", { description: `${p.name} is already at ${existing.qty} ${p.unit}.` });
    } else if (existing) {
      await setQty(p.product_id, p.sku, qty);
    } else {
      await addItem(p.product_id, p.sku, qty);
    }
    setBusyId(null);
  };

  return (
    <div className="mt-10" data-testid="buy-again-section" id="buy-again">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-amber-500">
            <span className="h-px w-10 bg-amber-500" /> Buy Again
          </p>
          <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-1">Quick reorder from past orders</h2>
        </div>
      </div>
      {products.length > 0 && (
        <div className="-mx-6 mt-5 flex gap-4 overflow-x-auto px-6 pb-2 lg:mx-0 lg:px-0">
          {products.map((p) => (
            <div key={`${p.product_id}-${p.sku}`} data-testid={`buy-again-item-${p.product_id}`}
              className="w-52 shrink-0 rounded-2xl border border-line bg-surface p-3 transition-colors hover:border-amber-500/25">
              <button onClick={() => navigate(`/shop/product/${p.product_id}`)} className="block w-full text-left">
                <img src={p.image} alt={p.name} loading="lazy" className="h-28 w-full rounded-xl bg-white object-contain" />
                <p className="mt-2.5 line-clamp-1 text-sm font-semibold text-1">{p.name}</p>
                <p className="mt-0.5 text-xs text-3">{p.variant_label}</p>
                <p className="mt-1 text-sm font-bold text-amber-400">{fmt(p.unit_price)}<span className="text-[10px] font-medium text-3"> / {p.unit}</span></p>
                <p className="mt-1 text-[10px] text-3" data-testid={`buy-again-freq-${p.product_id}`}>
                  Bought {p.times_purchased}×{p.last_purchased ? ` · last ${p.last_purchased}` : ""}
                </p>
              </button>
              <button onClick={() => quickAdd(p)} disabled={!p.in_stock || busyId === p.product_id + p.sku}
                data-testid={`buy-again-add-${p.product_id}`}
                className="mt-2.5 flex h-9 w-full items-center justify-center gap-1.5 rounded-lg bg-amber-500 text-xs font-bold text-black transition-colors hover:bg-amber-400 disabled:cursor-not-allowed disabled:border disabled:border-line disabled:bg-transparent disabled:text-3">
                {busyId === p.product_id + p.sku
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : p.in_stock ? <><ShoppingCart className="h-3.5 w-3.5" /> Add {Math.max(p.last_qty, p.min_order_qty || 1)} {p.unit}{Math.max(p.last_qty, p.min_order_qty || 1) > 1 && p.unit === "pc" ? "s" : ""}</> : "Out of stock"}
              </button>
            </div>
          ))}
        </div>
      )}
      {unavailable.length > 0 && (
        <div className="mt-4" data-testid="buy-again-unavailable">
          <p className="text-[11px] font-bold uppercase tracking-wider text-3">From your past orders — currently unavailable</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {unavailable.map((p) => (
              <div key={`${p.product_id}-${p.sku}-u`} data-testid={`buy-again-unavailable-${p.product_id}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-line bg-hover px-4 py-3 opacity-70">
                <div className="min-w-0">
                  <p className="truncate text-xs font-semibold text-2">{p.name} <span className="text-3">· {p.variant_label}</span></p>
                  <p className="mt-0.5 text-[11px] text-3" data-testid={`buy-again-reason-${p.product_id}`}>{p.unavailable_reason}</p>
                </div>
                {p.unit_price !== null && <p className="shrink-0 text-xs font-bold text-3">{fmt(p.unit_price)}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function ReorderReminders() {
  const { user } = useAuth();
  const { cart, addItem, setQty } = useStore();
  const [reminders, setReminders] = useState(null);
  const [busySku, setBusySku] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/orders/reorder-reminders")
      .then(({ data }) => setReminders(data.reminders))
      .catch(() => setReminders([]));
  }, [user]);

  if (!user || !reminders || reminders.length === 0) return null;

  const buyAgain = async (r) => {
    if (busySku || !r.suggested_qty) return;
    setBusySku(r.sku);
    try {
      // idempotent top-up: repeat clicks never stack beyond the suggested qty
      const existing = (cart?.items || []).find((i) => i.product_id === r.product_id && i.sku === r.sku);
      const current = existing?.qty || 0;
      if (current >= r.suggested_qty) {
        toast.info("Already in your cart", { description: `${r.name} is already at ${current} ${r.unit}.` });
      } else if (current > 0) {
        await setQty(r.product_id, r.sku, r.suggested_qty);
        toast.success(`${r.name} topped up to ${r.suggested_qty} ${r.unit} in your cart`);
      } else {
        await addItem(r.product_id, r.sku, r.suggested_qty, { silent: true });
        toast.success(`${r.name} added to your cart — review and check out when ready`);
      }
    } catch {
      toast.error("Couldn't add this item to your cart");
    } finally {
      setBusySku(null);
    }
  };

  return (
    <div className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="reorder-reminders">
      {reminders.map((r) => (
        <div key={`${r.product_id}-${r.sku}`} data-testid={`reminder-${r.sku}`}
          className={`rounded-2xl border p-4 ${r.available ? "border-amber-500/25 bg-amber-500/[0.04]" : "border-line bg-hover opacity-70"}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-1">{r.name}</p>
              <p className="text-xs text-3">{r.variant_label} · bought {r.times_purchased}× · usually {r.avg_qty} {r.unit}</p>
            </div>
            <span data-testid={`reminder-status-${r.sku}`}
              className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${r.status === "due" ? "border-amber-500/40 bg-amber-500/10 text-amber-300" : "border-sky-500/30 bg-sky-500/10 text-sky-300"}`}>
              {r.status === "due" ? "Restock due" : "Due soon"}
            </span>
          </div>
          <p className="mt-2 flex items-center gap-1.5 text-xs text-2">
            <AlarmClock className="h-3.5 w-3.5 text-amber-500/70" />
            {r.status === "due"
              ? `Estimated to have run out ${r.estimated_stockout} (${Math.max(0, -r.days_until_stockout)}d ago)`
              : `Estimated to run out around ${r.estimated_stockout} (in ${r.days_until_stockout}d)`}
          </p>
          {r.available ? (
            <div className="mt-3 flex items-center justify-between gap-3">
              <p className="text-sm font-bold text-amber-400">{fmt(r.unit_price)}<span className="text-[10px] font-medium text-3"> / {r.unit}</span></p>
              <button onClick={() => buyAgain(r)} disabled={busySku === r.sku} data-testid={`reminder-add-${r.sku}`}
                className="flex h-9 items-center gap-1.5 rounded-full bg-amber-500 px-4 text-xs font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-40">
                {busySku === r.sku ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShoppingCart className="h-3.5 w-3.5" />}
                Buy again · {r.suggested_qty} {r.unit}
              </button>
            </div>
          ) : (
            <p className="mt-3 text-xs text-3" data-testid={`reminder-reason-${r.sku}`}>{r.unavailable_reason}</p>
          )}
        </div>
      ))}
    </div>
  );
}
